import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import axiosInstance from '../utils/axiosInstance';
import {
    Container, Paper, Box, Typography, Stack, TextField, Button,
    IconButton, InputAdornment, Alert, Link
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const ApplySetPasswordPage = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const token = params.get('token');

    const [passwordVisible, setPasswordVisible] = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    const { register, handleSubmit, watch, formState: { errors } } = useForm();

    if (!token) {
        return (
            <Container maxWidth="sm">
                <Typography mt={6}>
                    このリンクはすでに無効です。<br />
                    お手数ですが、<Link component={RouterLink} to="/contact">お問い合わせページ</Link>よりメールの再送信をお願いします。
                </Typography>
            </Container>
        );
    }

    const onSubmit = async (data) => {
        setLoading(true);
        try {
            await axiosInstance.post('/set-password', { token, password: data.password });
            setSuccess(true);
        } catch (err) {
            alert(err.response?.data?.message || 'パスワード設定に失敗しました');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <Container maxWidth="sm">
                <Typography mt={6} align="center">
                    パスワードが設定されました。<br />
                    <Link component={RouterLink} to={`${process.env.REACT_APP_ADMIN_URL}/login`}>
                        ログインページ
                    </Link>へ移動してください。
                </Typography>
            </Container>
        );
    }

    const password = watch('password');

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 6 }}>
            <Container maxWidth="sm">
                <Paper variant='section' sx={{ p: 4, borderRadius: 2 }}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Stack spacing={2.5}>
                            <Typography variant="h4">パスワード設定</Typography>
                            <Typography variant="body2" color="text.secondary">
                                パスワードは英字と数字を組み合わせた8文字以上で設定してください。
                            </Typography>

                            <TextField
                                label="新しいパスワード"
                                type={passwordVisible ? 'text' : 'password'}
                                {...register('password', {
                                    required: 'パスワードを入力してください',
                                    pattern: {
                                        value: /^(?=.*[a-zA-Z])(?=.*\d).{8,}$/,
                                        message: '英数字混合で8文字以上で入力してください',
                                    },
                                })}
                                error={!!errors.password}
                                helperText={errors.password?.message}
                                inputProps={{ maxLength: 128 }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setPasswordVisible(v => !v)}>
                                                {passwordVisible ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <TextField
                                label="確認用パスワード"
                                type={confirmPasswordVisible ? 'text' : 'password'}
                                {...register('confirmPassword', {
                                    required: '確認用パスワードを入力してください',
                                    validate: value =>
                                        value === password || 'パスワードが一致しません',
                                })}
                                error={!!errors.confirmPassword}
                                helperText={errors.confirmPassword?.message}
                                inputProps={{ maxLength: 128 }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setConfirmPasswordVisible(v => !v)}>
                                                {confirmPasswordVisible ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Box display="flex" justifyContent="center" mt={3}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    sx={{ width: 200 }}
                                    disabled={loading}
                                >
                                    {loading ? '保存中…' : '保存'}
                                </Button>
                            </Box>
                        </Stack>
                    </form>
                </Paper>
            </Container>
        </Box>
    );
};

export default ApplySetPasswordPage;
